import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useStore } from '@/state/store';
import { tornado, type SensitivityInput, type SensitivityMetric } from '@/engine/sensitivity';
import { fmtCurrencyCompact } from '@/lib/format';
import { COLORS, useIsDark, TOOLTIP_PROPS } from './charts/chartUtils';
import { HowToRead } from './HowToRead';
import { CHART_HELP } from '@/content/chartHelp';

const ALL_INPUTS: SensitivityInput[] = ['monthly_draw', 'portfolio_return', 'base_rate', 'advance_rate', 'cap_gains_rate', 'horizon'];
const METRICS: { value: SensitivityMetric; label: string }[] = [
  { value: 'terminal_net_worth', label: 'Terminal net worth' },
  { value: 'total_interest', label: 'Total interest' },
  { value: 'worst_ltv', label: 'Worst LTV' },
  { value: 'heirs_difference', label: 'Estate difference' },
];

/** Sensitivity tornado (§6.17, §10.7). */
export function SensitivityPanel() {
  const inputs = useStore((s) => s.inputs);
  const bars = useStore((s) => s.tornado);
  const setTornado = useStore((s) => s.setTornado);
  const dark = useIsDark();
  const [metric, setMetric] = useState<SensitivityMetric>('terminal_net_worth');

  const run = () => {
    setTornado(tornado(inputs, metric, ALL_INPUTS));
  };

  const data = (bars ?? []).map((b) => {
    const lo = Math.min(b.low, b.high);
    const hi = Math.max(b.low, b.high);
    return { input: b.input, offset: lo, span: hi - lo, base: b.base };
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-2 text-sm font-semibold">Sensitivity (tornado)</h3>
      <div className="flex items-center gap-2 text-sm">
        <select className="rounded border border-slate-300 px-2 py-1 dark:border-slate-600 dark:bg-slate-900" value={metric} onChange={(e) => setMetric(e.target.value as SensitivityMetric)}>
          {METRICS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <button type="button" className="rounded bg-sky-600 px-3 py-1 text-white" onClick={run}>Run</button>
      </div>
      {data.length > 0 && (
        <>
          <div className="mt-3 h-64">
            <ResponsiveContainer>
              <BarChart layout="vertical" data={data} margin={{ top: 5, right: 12, bottom: 0, left: 8 }}>
                <CartesianGrid stroke={dark ? '#334155' : '#e2e8f0'} strokeDasharray="3 3" />
                <XAxis type="number" stroke={dark ? '#94a3b8' : '#64748b'} fontSize={11} tickFormatter={fmtCurrencyCompact} />
                <YAxis type="category" dataKey="input" width={110} stroke={dark ? '#94a3b8' : '#64748b'} fontSize={11} />
                <Tooltip {...TOOLTIP_PROPS} cursor={false} formatter={(v: number | string, n) => (n === 'span' ? fmtCurrencyCompact(Number(v)) : v)} />
                {data[0] && <ReferenceLine x={data[0].base} stroke={COLORS.danger} strokeDasharray="4 2" />}
                <Bar dataKey="offset" stackId="t" fill="transparent" />
                <Bar dataKey="span" stackId="t">
                  {data.map((_, i) => <Cell key={i} fill={COLORS.netWorth} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <HowToRead text={CHART_HELP.tornado} />
        </>
      )}
    </div>
  );
}
